import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Field,
  Input,
  Textarea,
  Card,
  Icon,
  Progress,
  SimpleGrid
} from '@chakra-ui/react'
import { FiArrowRight, FiArrowLeft, FiCheck } from 'react-icons/fi'
import { OnboardingLayout } from '../../components/onboarding/OnboardingLayout'
import { VisibilityScore } from '../../components/onboarding/VisibilityScore'
import { useOnboarding } from '../../contexts/OnboardingContext'
import { ImproveableTextarea } from '../../components/onboarding/ImproveableTextarea'

interface InterviewBlock {
  id: number
  title: string
  description: string
  fields: Array<{
    key: string
    label: string
    type: 'text' | 'textarea' | 'multiselect'
    options?: string[]
    placeholder?: string
  }>
  required: boolean
}

const INTERVIEW_BLOCKS: InterviewBlock[] = [
  {
    id: 1,
    title: 'Ideal Client',
    description: 'Help us understand who you serve best',
    required: false,
    fields: [
      {
        key: 'ideal_client',
        label: 'Who do you help?',
        type: 'textarea',
        placeholder: 'Describe your ideal client - their age, challenges, goals, etc.'
      },
      {
        key: 'problems_solved',
        label: 'What problem do they come to you for?',
        type: 'textarea',
        placeholder: 'What are the main problems or challenges your clients face?'
      },
      {
        key: 'transformation',
        label: "What's the #1 transformation you offer?",
        type: 'textarea',
        placeholder: 'What is the primary outcome or transformation clients experience?'
      }
    ]
  },
  {
    id: 2,
    title: 'Your Approach',
    description: 'Tell us about your methods and expertise',
    required: false,
    fields: [
      {
        key: 'approach',
        label: 'How do you typically work with clients?',
        type: 'textarea',
        placeholder: 'Describe your coaching or treatment approach'
      },
      {
        key: 'tools_methods',
        label: 'List any tools, methods, or certifications',
        type: 'text',
        placeholder: 'e.g., Functional Medicine, CBT, Nutrition Planning'
      }
    ]
  },
  {
    id: 3,
    title: 'Offers',
    description: 'What do you offer to new clients?',
    required: false,
    fields: [
      {
        key: 'offers',
        label: 'Do you offer free consults?',
        type: 'text',
        placeholder: 'Yes/No and details'
      },
      {
        key: 'packages',
        label: 'Your main package(s)',
        type: 'textarea',
        placeholder: 'Describe your main service packages or programs'
      }
    ]
  },
  {
    id: 4,
    title: 'Personal Tone',
    description: 'How should your ad sound?',
    required: false,
    fields: [
      {
        key: 'tone_preferences',
        label: 'What should the ad emphasize? (Select all that apply)',
        type: 'multiselect',
        options: ['Trust', 'Warmth', 'Professional', 'Evidence-based', 'Transformational']
      }
    ]
  },
  {
    id: 5,
    title: 'Optional Enhancers',
    description: 'Add testimonials, credentials, and more',
    required: false,
    fields: [
      {
        key: 'testimonials',
        label: 'Testimonials (one per line)',
        type: 'textarea',
        placeholder: 'Paste client testimonials here, one per line'
      },
      {
        key: 'credentials',
        label: 'Credentials',
        type: 'text',
        placeholder: 'e.g., Certified Nutritionist, Licensed Therapist'
      },
      {
        key: 'specialties',
        label: 'Additional Specialties',
        type: 'text',
        placeholder: 'Any additional specialties or areas of expertise'
      }
    ]
  }
]

export const DetailedInterviewPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    detailed_interview,
    visibility_score,
    updateDetailedInterview,
    saveDetailedInterviewBlock,
    isSaving,
    error,
    loadProgress
  } = useOnboarding()

  const [currentBlockIndex, setCurrentBlockIndex] = useState(0)
  const [blockData, setBlockData] = useState<Record<string, any>>({})
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const currentBlock = INTERVIEW_BLOCKS[currentBlockIndex]
  const totalBlocks = INTERVIEW_BLOCKS.length

  // Get path prefix from current route
  const getPathPrefix = () => {
    if (location.pathname.startsWith('/for-products/onboarding')) return '/for-products/onboarding'
    if (location.pathname.startsWith('/for-services/onboarding')) return '/for-services/onboarding'
    return '/for-services/onboarding' // Default fallback
  }
  const pathPrefix = getPathPrefix()

  useEffect(() => {
    // Initialize block data from context
    const initialData: Record<string, any> = {}
    currentBlock.fields.forEach((field) => {
      const value = detailed_interview[field.key as keyof typeof detailed_interview]
      if (value !== undefined) {
        initialData[field.key] = value
      }
    })
    setBlockData(initialData)
    setHasUnsavedChanges(false)
  }, [currentBlockIndex, currentBlock, detailed_interview])

  const handleFieldChange = (key: string, value: any) => {
    setBlockData((prev) => ({ ...prev, [key]: value }))
    setHasUnsavedChanges(true)
    // Update context immediately for live score updates
    updateDetailedInterview({ [key]: value })
  }

  const handleNext = async () => {
    if (hasUnsavedChanges) {
      try {
        await saveDetailedInterviewBlock(blockData)
        setHasUnsavedChanges(false)
      } catch (err) {
        console.error('Failed to save block:', err)
        return
      }
    }

    if (currentBlockIndex < totalBlocks - 1) {
      setCurrentBlockIndex(currentBlockIndex + 1)
    } else {
      // All blocks complete, navigate to assets page
      navigate(`${pathPrefix}/assets`)
    }
  }

  const handlePrevious = () => {
    if (currentBlockIndex > 0) {
      setCurrentBlockIndex(currentBlockIndex - 1)
    } else {
      navigate(`${pathPrefix}/preview`)
    }
  }

  const handleSkip = () => {
    if (currentBlockIndex < totalBlocks - 1) {
      setCurrentBlockIndex(currentBlockIndex + 1)
    } else {
      navigate(`${pathPrefix}/assets`)
    }
  }

  const renderField = (field: InterviewBlock['fields'][0]) => {
    const value = blockData[field.key] || ''

    switch (field.type) {
      case 'textarea':
        return (
          <ImproveableTextarea
            value={value}
            onChange={(value) => handleFieldChange(field.key, value)}
            context={field.key}
            fieldType="general"
            placeholder={field.placeholder}
            rows={4}
            size="lg"
          />
        )
      case 'multiselect':
        const selectedValues = Array.isArray(value) ? value : []
        return (
          <VStack align="start" gap={2}>
            {field.options?.map((option) => (
              <label
                key={option}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const updated = e.target.checked
                      ? [...selectedValues, option]
                      : selectedValues.filter((v) => v !== option)
                    handleFieldChange(field.key, updated)
                  }}
                  style={{ marginRight: '8px' }}
                />
                <Text as="span">{option}</Text>
              </label>
            ))}
          </VStack>
        )
      default:
        return (
          <Input
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            size="lg"
          />
        )
    }
  }

  return (
    <OnboardingLayout
      currentStep={4}
      totalSteps={5}
      stepLabels={['Basic Setup', 'Preview', 'Interview', 'Assets', 'Select package']}
    >
      <VStack gap={{ base: 6, md: 8 }} align="stretch" maxW="4xl" mx="auto" px={{ base: 4, sm: 6, lg: 8 }} pb={{ base: 16, md: 24 }}>
        {/* Header */}
        <VStack gap={4} textAlign="center">
          <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900">
            {currentBlock.title}
          </Heading>
          <Text fontSize="lg" color="gray.600">
            {currentBlock.description}
          </Text>
          <Text fontSize="sm" color="gray.500">
            Block {currentBlockIndex + 1} of {totalBlocks}
          </Text>
        </VStack>

        {/* Progress Bar */}
        <Box>
          <Progress.Root value={((currentBlockIndex + 1) / totalBlocks) * 100} colorPalette="brand" size="lg">
            <Progress.Track borderRadius="full">
              <Progress.Range />
            </Progress.Track>
          </Progress.Root>
        </Box>

        {/* Error Display */}
        {error && (
          <Box
            p={4}
            bg="red.50"
            borderWidth="1px"
            borderColor="red.200"
            borderRadius="md"
            color="red.700"
          >
            <Text fontSize="sm">{error}</Text>
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, lg: 3 }} gap={{ base: 6, md: 8 }}>
          {/* Main Form */}
          <Box gridColumn={{ base: '1', lg: 'span 2' }}>
            <Card.Root>
              <Card.Body p={{ base: 5, md: 8 }}>
                <VStack gap={{ base: 5, md: 6 }} align="stretch">
                  {currentBlock.fields.map((field) => (
                    <Field.Root key={field.key}>
                      <Field.Label>{field.label}</Field.Label>
                      {renderField(field)}
                    </Field.Root>
                  ))}
                </VStack>
              </Card.Body>
            </Card.Root>
          </Box>

          {/* Sidebar - Visibility Score */}
          <Box>
            <VStack gap={4} align="stretch" position="sticky" top={4}>
              <VisibilityScore
                score={visibility_score.current}
                breakdown={visibility_score.breakdown}
                showBreakdown={true}
              />
              {hasUnsavedChanges && (
                <Box
                  p={3}
                  bg="yellow.50"
                  borderWidth="1px"
                  borderColor="yellow.200"
                  borderRadius="md"
                >
                  <Text fontSize="xs" color="yellow.700">
                    You have unsaved changes
                  </Text>
                </Box>
              )}
            </VStack>
          </Box>
        </SimpleGrid>

        {/* Navigation Buttons */}
        <HStack justify="space-between" pt={{ base: 4, md: 6 }} gap={{ base: 2, md: 4 }} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
          <Button
            variant="ghost"
            leftIcon={<Icon as={FiArrowLeft} />}
            onClick={handlePrevious}
            size={{ base: 'md', md: 'lg' }}
          >
            Previous
          </Button>
          <HStack gap={{ base: 2, md: 4 }}>
            {!currentBlock.required && (
              <Button variant="outline" onClick={handleSkip} size={{ base: 'md', md: 'lg' }}>
                Skip
              </Button>
            )}
            <Button
              colorPalette="brand"
              rightIcon={<Icon as={currentBlockIndex === totalBlocks - 1 ? FiCheck : FiArrowRight} />}
              onClick={handleNext}
              isDisabled={isSaving}
              size={{ base: 'md', md: 'lg' }}
            >
              {isSaving
                ? 'Saving...'
                : currentBlockIndex === totalBlocks - 1
                ? 'Complete'
                : 'Next'}
            </Button>
          </HStack>
        </HStack>
      </VStack>
    </OnboardingLayout>
  )
}





